import { createFilterOptions } from "@mui/material";
import { useEffect, useState } from "react";
import {
  useGroupsQuery,
  useMoveGroupMembersMutation,
  useSaveGroupMutation,
} from "../../../../App/Queries/GroupQueries";
import { useCurrentUser } from "../../../../App/Queries/UserQueries";
import { useNotification } from "../../../../App/State/Hooks/notificationHooks";
import { ISelectedCandidate } from "../../../../App/State/Slices/MessageDialogSlice";
import { IGroup, IGroupFilter, IGroupMember } from "../../../../App/Typings/Groups";
import { useDebouncedHandler } from "../../../../App/State/Hooks/useDebouncedHandler";

export function useMoveToGroupState(
  groupId: string,
  candidatesToAddToGroup: ISelectedCandidate[],
  setModalOpen: (isOpen: boolean) => void,
  setCandidatesToAddToGroup: (candidates: string[]) => void,
) {
  const user = useCurrentUser();
  const { data: groups } = useGroupsQuery(false, (pagination) => ({
    ...pagination,
    items: pagination.items.filter((group) => group.id !== groupId),
  }));
  const { mutateAsync: moveGroupMembers, isLoading: isLoadingMoveMembers } = useMoveGroupMembersMutation();
  const { mutateAsync: saveGroup, isLoading: isLoadingSaveGroup } = useSaveGroupMutation("");
  const { displayError, displaySuccess, displayWarning } = useNotification();

  const [groupDropdownList, setGroupDropdownList] = useState<IGroupFilter[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<IGroupFilter>({} as IGroupFilter);
  const [selectedGroupData, setSelectedGroupData] = useState<IGroup | undefined>({} as IGroup);

  useEffect(() => {
    if (groups && groups.items.length > 0) {
      setGroupDropdownList(
        groups.items
          .filter((c) => !c.isDynamic)
          .map(
            (group) =>
              ({
                id: group.id,
                name: group.name,
              }) as IGroupFilter,
          ),
      );
    }
  }, [groups]);

  useEffect(() => {
    if (selectedGroup.id && selectedGroup.id !== "") {
      setSelectedGroupData((prevData) => {
        if (prevData?.id === selectedGroup.id) {
          return prevData;
        }
        return groups?.items.find((group) => group.id === selectedGroup.id);
      });
    }
  }, [groups, selectedGroup]);

  const autoCompleteFilter = createFilterOptions<IGroupFilter>();

  const handleCancelMoveToGroup = () => {
    setModalOpen(false);
    setSelectedGroup({} as IGroupFilter);
  };

  /**
   * @description stores currently selected group
   * @param event
   * @param newValue
   */
  const handleGroupSelect = (event: React.SyntheticEvent, newValue: any, reason: string, details: any) => {
    if (reason === "clear") {
      setSelectedGroup({
        id: "",
        name: "",
      });
    } else if (typeof newValue === "string") {
      setSelectedGroup({
        id: "",
        name: newValue,
      });
    } else if (newValue) {
      newValue.name = newValue.name.replace(/^Add "(.*?)"$/gi, "$1");
      setSelectedGroup(details.option);
    }
  };

  const handleGroupSave = useDebouncedHandler(async () => {
    if (!selectedGroup.name) {
      displayWarning("Please select a group from the dropdown");
      return;
    } else if (candidatesToAddToGroup.length === 0) {
      displayWarning("Please select at least one candidate to move to the group");
      return;
    }
    const isNewGroup = !selectedGroupData?.id;
    const groupMembers: IGroupMember[] = [];

    candidatesToAddToGroup.forEach((candidate) => {
      const candidateGroupData: Partial<IGroupMember> = {
        addedById: user.id,
        candidateId: candidate.candidateId,
        firstName: candidate.firstName,
        lastName: candidate.lastName,
      };
      if (!isNewGroup) {
        candidateGroupData.groupId = selectedGroup.id;
      }
      groupMembers.push(candidateGroupData as IGroupMember);
    });

    if (isNewGroup) {
      await saveGroup(
        {
          name: selectedGroup.name,
          members: groupMembers,
          id: selectedGroup.id === "" ? undefined : selectedGroup.id,
          isDynamic: false,
          rules: JSON.stringify([]),
        },
        {
          onError: () => displayError("There was an error saving group candidates. Please try again"),
          onSuccess: () => {
            setModalOpen(false);
            displaySuccess("Group candidates saved successfully!");
            setCandidatesToAddToGroup([]);
          },
        },
      );
    } else {
      await moveGroupMembers(
        {
          candidateIds: groupMembers.map((c) => c.candidateId ?? ""),
          groupId,
          targetGroupId: selectedGroup.id,
        },
        {
          onError: () => displayError("There was an error moving group candidates. Please try again"),
          onSuccess: () => {
            setModalOpen(false);
            displaySuccess("Group candidates moved successfully!");
            setCandidatesToAddToGroup([]);
          },
        },
      );
    }
  }, 300);

  return {
    getters: {
      autoCompleteFilter,
      groupDropdownList,
      selectedGroup,
      isLoadingMoveMembers,
      isLoadingSaveGroup,
    },
    handlers: {
      handleCancelMoveToGroup,
      handleGroupSave,
      handleGroupSelect,
    },
    setters: {
      setGroupDropdownList,
      setSelectedGroupData,
    },
  } as const;
}
